import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Download, Upload, FileJson, CheckCircle2, AlertCircle } from "lucide-react";
import { Json } from "@/integrations/supabase/types";

interface JsonBlock {
  block_type: string;
  content: Record<string, any>;
}

interface JsonArticleTemplate {
  project_name: string;
  slug?: string;
  system_name?: string;
  hero_image_url?: string;
  client_name?: string;
  industry?: string;
  location?: string;
  area?: string;
  tag_light_condition?: string;
  tag_spatial_type?: string;
  tag_material_type?: string;
  light_condition?: string;
  environmental_condition?: string;
  material_constraint?: string;
  core_material_strategy?: string;
  challenge_text?: string;
  surface_calibration_headline?: string;
  surface_calibration_text?: string;
  material_composition_headline?: string;
  material_composition_text?: string;
  execution_complexity_headline?: string;
  execution_complexity_text?: string;
  cta_headline?: string;
  cta_button_text?: string;
  published?: boolean;
  blocks?: JsonBlock[];
}

const EXAMPLE_TEMPLATE: JsonArticleTemplate[] = [
  {
    project_name: "Riverside Office Tower",
    slug: "riverside-office-tower",
    system_name: "System V3",
    hero_image_url: "https://example.com/hero.jpg",
    client_name: "Riverside Corp",
    industry: "Commercial",
    location: "Singapore",
    area: "1200 sqm",
    tag_light_condition: "Daylight",
    tag_spatial_type: "Open Plan",
    tag_material_type: "Wood + Stone",
    published: false,
    blocks: [
      {
        block_type: "text_section",
        content: {
          title: "Project Overview",
          body: "Balancing acoustic performance with aesthetic warmth in a large open-plan space.",
          showDivider: true,
        },
      },
      {
        block_type: "image",
        content: {
          imageUrl: "https://example.com/detail-shot.jpg",
          caption: "Detail of oak panel installation",
          alignment: "full",
        },
      },
      {
        block_type: "two_column",
        content: {
          title: "Material Strategy",
          text: "Natural grain contrast with polished stone for visual rhythm.",
          imageUrl: "https://example.com/strategy.jpg",
          reversed: false,
        },
      },
      {
        block_type: "image_hotspot",
        content: {
          imageUrl: "https://example.com/floorplan.jpg",
          hotspots: [
            { x: 30, y: 40, label: "Reception Desk", description: "Engineered oak with brass inlay" },
            { x: 70, y: 60, label: "Feature Wall", description: "Natural stone cladding" },
          ],
        },
      },
      {
        block_type: "gallery",
        content: {
          images: [
            { url: "https://example.com/gallery-1.jpg", caption: "Lobby entrance" },
            { url: "https://example.com/gallery-2.jpg", caption: "Open workspace" },
          ],
        },
      },
      {
        block_type: "material_reference",
        content: { materialIds: [] },
      },
      {
        block_type: "cta",
        content: {
          headline: "Submit Your Project For System Matching",
          buttonText: "Request Review",
        },
      },
    ],
  },
];

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

function downloadTemplate() {
  const json = JSON.stringify(EXAMPLE_TEMPLATE, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "case_articles_template.json";
  a.click();
  URL.revokeObjectURL(url);
}

interface ImportResult {
  name: string;
  success: boolean;
  blockCount?: number;
  error?: string;
}

export function JsonTemplateImport() {
  const [results, setResults] = useState<ImportResult[]>([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setResults([]);

    try {
      const text = await file.text();
      let articles: JsonArticleTemplate[];

      try {
        const parsed = JSON.parse(text);
        articles = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        toast({ title: "Error", description: "Invalid JSON file.", variant: "destructive" });
        setImporting(false);
        return;
      }

      const importResults: ImportResult[] = [];

      for (const article of articles) {
        if (!article.project_name) {
          importResults.push({ name: "Unknown", success: false, error: "Missing project_name" });
          continue;
        }

        const slug = article.slug || slugify(article.project_name);

        try {
          const { data: created, error: articleError } = await supabase
            .from("case_articles")
            .insert({
              project_name: article.project_name,
              slug,
              system_name: article.system_name || null,
              hero_image_url: article.hero_image_url || null,
              client_name: article.client_name || null,
              industry: article.industry || null,
              location: article.location || null,
              area: article.area || null,
              tag_light_condition: article.tag_light_condition || null,
              tag_spatial_type: article.tag_spatial_type || null,
              tag_material_type: article.tag_material_type || null,
              light_condition: article.light_condition || null,
              environmental_condition: article.environmental_condition || null,
              material_constraint: article.material_constraint || null,
              core_material_strategy: article.core_material_strategy || null,
              challenge_text: article.challenge_text || null,
              surface_calibration_headline: article.surface_calibration_headline || null,
              surface_calibration_text: article.surface_calibration_text || null,
              material_composition_headline: article.material_composition_headline || null,
              material_composition_text: article.material_composition_text || null,
              execution_complexity_headline: article.execution_complexity_headline || null,
              execution_complexity_text: article.execution_complexity_text || null,
              cta_headline: article.cta_headline || null,
              cta_button_text: article.cta_button_text || null,
              published: article.published ?? false,
            })
            .select("id")
            .single();

          if (articleError) throw articleError;

          const blocks = article.blocks || [];
          if (blocks.length > 0) {
            const rows = blocks.map((b, i) => ({
              article_id: created.id,
              block_type: b.block_type,
              sort_order: i,
              content: (b.content || {}) as Json,
            }));
            const { error: blocksError } = await supabase.from("case_article_blocks").insert(rows);
            if (blocksError) throw blocksError;
          }

          importResults.push({ name: article.project_name, success: true, blockCount: blocks.length });
        } catch (err: any) {
          importResults.push({ name: article.project_name, success: false, error: err.message });
        }
      }

      setResults(importResults);
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });

      const successCount = importResults.filter((r) => r.success).length;
      toast({
        title: "Import Complete",
        description: `${successCount} of ${importResults.length} articles imported successfully.`,
      });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-2 font-sans text-xs uppercase tracking-wider">
          <Download size={14} /> JSON Template
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="gap-2 font-sans text-xs uppercase tracking-wider"
        >
          <Upload size={14} /> {importing ? "Importing…" : "Upload JSON"}
        </Button>
        <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFile} />
      </div>

      {results.length > 0 && (
        <div className="border border-border bg-background p-4 space-y-2 max-h-[300px] overflow-y-auto">
          <p className="font-sans text-xs font-medium text-foreground flex items-center gap-2">
            <FileJson size={14} /> Import Results
          </p>
          {results.map((r, i) => (
            <div key={i} className="flex items-start gap-2 font-sans text-xs">
              {r.success ? (
                <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle size={14} className="text-destructive mt-0.5 flex-shrink-0" />
              )}
              <span className="text-foreground">
                {r.name}
                {r.success && r.blockCount !== undefined && (
                  <span className="text-muted-foreground ml-1">({r.blockCount} blocks)</span>
                )}
              </span>
              {r.error && <span className="text-muted-foreground">— {r.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
